import { useStore } from '@doltech/core/lib/entity/store';
import { Instance, types as t } from 'mobx-state-tree';
import * as React from 'react';

import { StepModel } from './step-model';

export const BaseStep = t
  .model('BaseStep', {
    steps: t.maybe(t.array(StepModel)),
    currentStep: t.optional(t.number, 0),
    stepLoading: t.optional(t.boolean, false),
    inCreateMode: t.optional(t.boolean, false),
    allStepReadOnly: t.optional(t.boolean, false),
  })
  .volatile(() => ({
    stepsComponent: null,
    injectStepComponents: [],
  }))
  .actions((self) => ({
    setAllStepReadOnly(isReadOnly: boolean) {
      self.allStepReadOnly = isReadOnly;
    },
    replaceSteps(steps) {
      self.steps.replace(
        steps.map((step, stepIndex) => {
          const existStep = self.steps?.[stepIndex] || {};
          return {
            ...existStep,
            ...step,
          };
        })
      );
    },
    registerInjectStepsComponent(injectStepComponents) {
      self.injectStepComponents = injectStepComponents;
    },
    switchStepsComponentById(id) {
      const findStep = self.injectStepComponents.find((item) => item.id === id);
      if (findStep) {
        self.stepsComponent = findStep.stepsComponent;
      }
    },
    prevStep() {
      self.currentStep -= 1;
    },
    nextStep() {
      if (self.steps[self.currentStep].isValid) {
        self.currentStep += 1;
      }
    },
    goToStep(stepIndex: number) {
      if (self.inCreateMode) {
        return;
      }
      if (stepIndex > 0) {
        const stepsClone = self.steps.slice(0, stepIndex);
        const isValid = stepsClone.every((step) => step.isValid);
        if (isValid) {
          self.currentStep = stepIndex;
        }
      } else {
        self.currentStep = stepIndex;
      }
    },
    setStepLoading(loading: boolean) {
      self.stepLoading = loading;
    },
    setInCreateMode() {
      self.inCreateMode = true;
    },
    // Please keep these function empty, these for subscribe mobx action
    validateNextStep() {},
    validateNextError() {},
    validateStep() {},
    validateGoToStep(stepIndex) {
      return stepIndex;
    },
    forceGoToStep(stepIndex) {
      self.currentStep = stepIndex;
    },
    validatePreviousStep() {},
    validateSubmit() {},
  }))
  .views((self) => ({
    get hasPrev() {
      return self.currentStep > 0;
    },
    get hasNext() {
      return self.currentStep < self.steps.length - 1;
    },
    get isFinal() {
      return self.currentStep === self.steps.length - 1;
    },
    getCurrentStep() {
      return self.steps[self.currentStep];
    },
    isCurrentStepValid() {
      return self.steps[self.currentStep].isValid;
    },
    isCurrentStepCanSave() {
      return self.steps[self.currentStep].canSave;
    },
  }));

export interface BaseStepInstance extends Instance<typeof BaseStep> {}

export const BaseStepStoreContext = React.createContext(null);
// Export hook.tsx to use
export const useStepStore = <S extends BaseStepInstance>() => {
  const { stepStore } = useStore<{ stepStore: S }>(BaseStepStoreContext);
  return stepStore;
};
